/**
 * Spinner — on-brand neutral hairline loading indicator.
 *
 * Design rules (from DESIGN.md):
 *   - Track:     --ap-rule   (neutral hairline, warm gray)
 *   - Indicator: --ap-muted  (secondary text color)
 *   - Pink is brand-only — NEVER use T.accent for loading states.
 *   - Animation is gated on prefers-reduced-motion: the spinner is still
 *     rendered (for sighted layout) but the spin is suppressed.
 *
 * Capacitor readiness: pure CSS, no web APIs, no platform divergence.
 */

import type { CSSProperties } from 'react';

const spinKeyframes = `@keyframes gg-spin { to { transform: rotate(360deg); } }`;

const reducedMotionKeyframes = `
  @media (prefers-reduced-motion: reduce) {
    .gg-spin-el { animation: none !important; }
  }
`;

interface SpinnerProps {
  /** Diameter in px. Defaults to 32. */
  size?: number;
  /** aria-label for screen readers. Defaults to "Loading". */
  label?: string;
  style?: CSSProperties;
}

export function Spinner({ size = 32, label = 'Loading', style }: SpinnerProps) {
  const thickness = Math.max(2, Math.round(size / 10));

  return (
    <>
      <style>{spinKeyframes + reducedMotionKeyframes}</style>
      <div
        role="status"
        aria-label={label}
        className="gg-spin-el"
        style={{
          width: size,
          height: size,
          border: `${thickness}px solid var(--ap-rule)`,
          borderTopColor: 'var(--ap-muted)',
          borderRadius: '50%',
          animation: 'gg-spin 0.8s linear infinite',
          margin: '0 auto',
          flexShrink: 0,
          ...style,
        }}
      />
    </>
  );
}
